import Button from '@/components/Button';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import { animationConfig, getPageAnimation } from '@/config/animations';
import { motion } from 'framer-motion';
import { Cloud, Code, Rocket, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const AnimationWrapper = animationConfig.enablePageTransitions ? motion.div : 'div';

  const values = [
    {
      icon: Code,
      title: 'Full-Stack Expertise',
      description: "From React frontends to AWS serverless backends, we build complete, production-ready applications.",
    },
    {
      icon: Cloud,
      title: 'Cloud-Native Solutions',
      description: "Specialized in AWS serverless architecture, infrastructure as code, and scalable cloud deployments.",
    },
    {
      icon: Rocket,
      title: 'Rapid Delivery',
      description: "From concept to deployment, we move efficiently to deliver working solutions quickly.",
    },
    {
      icon: Target,
      title: 'Modern Technologies',
      description: "React, TypeScript, Node.js, .NET, PostgreSQL, DynamoDB, Docker, and the latest development tools.",
    },
  ];

  return (
    <AnimationWrapper
      {...getPageAnimation()}
      className="min-h-screen bg-gradient-to-b from-dark-950 via-dark-900 to-dark-950"
    >
      <Navbar />

      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-gradient">NiroLabs</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Full-stack development consultancy specializing in modern web applications,
              AWS cloud infrastructure, and scalable solutions for businesses.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="glass-effect rounded-3xl p-12 mb-20"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  What We Do
                </h2>
                <p className="text-gray-300 mb-4">
                  We build modern web applications from the ground up — from responsive React frontends
                  to scalable AWS serverless backends. Our focus is on delivering production-ready solutions
                  using the latest technologies and best practices.
                </p>
                <p className="text-gray-300 mb-4">
                  Specializing in full-stack development, cloud infrastructure, and database design, we help
                  businesses build reliable, scalable applications. From initial concept to deployment and
                  beyond, we handle the entire development lifecycle.
                </p>
                <p className="text-gray-300 mb-4">
                  We're also building <strong className="text-white">VisualForge</strong>, an AI-powered
                  creative platform currently in early access. Whether you need a complete web application,
                  cloud infrastructure setup, or custom development services, we're here to help.
                </p>
                <Link to="/contact">
                  <Button className="mt-4">
                    Get in Touch
                  </Button>
                </Link>
              </div>
              <div className="glass-effect p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-white mb-6">Services Available</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-300">Full-Stack Development</span>
                      <span className="text-primary-400">Available</span>
                    </div>
                    <div className="w-full bg-dark-800 rounded-full h-2">
                      <div className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-300">AWS Cloud Solutions</span>
                      <span className="text-primary-400">Available</span>
                    </div>
                    <div className="w-full bg-dark-800 rounded-full h-2">
                      <div className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-300">VisualForge (Early Access)</span>
                      <span className="text-primary-400">Beta</span>
                    </div>
                    <div className="w-full bg-dark-800 rounded-full h-2">
                      <div className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full" style={{ width: '60%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Our Approach
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-effect p-6 rounded-xl text-center"
                >
                  <value.icon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-400">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center glass-effect rounded-3xl p-12"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Let's Work Together
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Need help with a web application, cloud infrastructure, or custom development project?
              Let's discuss how we can help bring your ideas to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg">
                  Start a Project
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" size="lg">
                  View Services
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </AnimationWrapper>
  );
};

export default AboutPage;